"use client";

import type { Session, User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { createClient as createSupabaseBrowserClient } from "@/lib/supabase/client";

/**
 * Return type for the useAuth hook
 */
export type UseAuthReturn = {
  /** The currently authenticated user, or null if not authenticated */
  user: User | null;
  /** The current session object, or null if no active session */
  session: Session | null;
  /** Whether the authentication state is still being loaded */
  loading: boolean;
};

/**
 * Hook for managing Supabase authentication state in client components.
 *
 * This hook provides real-time access to the current user's authentication state,
 * automatically subscribing to auth state changes and cleaning up on unmount.
 *
 * @returns An object containing the current user, session, and loading state
 *
 * @example
 * ```tsx
 * function ProtectedComponent() {
 *   const { user, session, loading } = useAuth();
 *
 *   if (loading) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (!user) {
 *     return <div>Please sign in</div>;
 *   }
 *
 *   return <div>Welcome, {user.email}</div>;
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createSupabaseBrowserClient();

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, loading };
}
